import React, {useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {HugeiconsIcon} from '@hugeicons/react';
import {Cancel01Icon, CheckIcon, Copy01Icon, KeyboardIcon} from '@hugeicons/core-free-icons';
import {grey} from '@mui/material/colors';
import {VocabularyRepository} from '../../../../repositories/VocabularyRepository';
import {toast} from 'react-toastify';

const PHONEMIZER_CHARS = [
  // Vowels
  {symbol: 'a',  ipa: 'a',   description: 'open front unrounded — father'},
  {symbol: 'e',  ipa: 'e',   description: 'close-mid front — bed'},
  {symbol: 'i',  ipa: 'iː',  description: 'close front — see'},
  {symbol: 'o',  ipa: 'o',   description: 'close-mid back — go'},
  {symbol: 'u',  ipa: 'uː',  description: 'close back — food'},
  {symbol: '@',  ipa: 'ə',   description: 'schwa — about'},
  {symbol: '{',  ipa: 'æ',   description: 'near-open front — cat'},
  {symbol: 'V',  ipa: 'ʌ',   description: 'open-mid back — cup'},
  {symbol: 'A',  ipa: 'ɑː',  description: 'open back — father (long)'},
  {symbol: 'E',  ipa: 'ɛ',   description: 'open-mid front — dress'},
  {symbol: 'I',  ipa: 'ɪ',   description: 'near-close near-front — kit'},
  {symbol: 'O',  ipa: 'ɔ',   description: 'open-mid back rounded — lot'},
  {symbol: 'U',  ipa: 'ʊ',   description: 'near-close near-back — foot'},
  {symbol: '3',  ipa: 'ɜː',  description: 'open-mid central — nurse'},
  {symbol: 'y',  ipa: 'y',   description: 'close front rounded — French tu'},
  {symbol: '2',  ipa: 'ø',   description: 'close-mid front rounded — French feu'},
  // Consonants
  {symbol: 'p',  ipa: 'p',   description: 'voiceless bilabial stop — pen'},
  {symbol: 'b',  ipa: 'b',   description: 'voiced bilabial stop — boy'},
  {symbol: 't',  ipa: 't',   description: 'voiceless alveolar stop — top'},
  {symbol: 'd',  ipa: 'd',   description: 'voiced alveolar stop — dog'},
  {symbol: 'k',  ipa: 'k',   description: 'voiceless velar stop — king'},
  {symbol: 'g',  ipa: 'g',   description: 'voiced velar stop — go'},
  {symbol: 'f',  ipa: 'f',   description: 'voiceless labiodental — fish'},
  {symbol: 'v',  ipa: 'v',   description: 'voiced labiodental — van'},
  {symbol: 'T',  ipa: 'θ',   description: 'voiceless dental — think'},
  {symbol: 'D',  ipa: 'ð',   description: 'voiced dental — this'},
  {symbol: 's',  ipa: 's',   description: 'voiceless alveolar fricative — sun'},
  {symbol: 'z',  ipa: 'z',   description: 'voiced alveolar fricative — zoo'},
  {symbol: 'S',  ipa: 'ʃ',   description: 'voiceless postalveolar — shoe'},
  {symbol: 'Z',  ipa: 'ʒ',   description: 'voiced postalveolar — measure'},
  {symbol: 'h',  ipa: 'h',   description: 'glottal fricative — hat'},
  {symbol: 'x',  ipa: 'x',   description: 'velar fricative — loch'},
  {symbol: 'tS', ipa: 'tʃ',  description: 'voiceless postalveolar affricate — church'},
  {symbol: 'dZ', ipa: 'dʒ',  description: 'voiced postalveolar affricate — judge'},
  {symbol: 'ts', ipa: 'ts',  description: 'voiceless alveolar affricate — cats'},
  {symbol: 'dz', ipa: 'dz',  description: 'voiced alveolar affricate'},
  {symbol: 'tC', ipa: 'tɕ',  description: 'voiceless palatal affricate — soft ch'},
  {symbol: 'dJ', ipa: 'dʑ',  description: 'voiced palatal affricate — soft j'},
  {symbol: 'm',  ipa: 'm',   description: 'bilabial nasal — man'},
  {symbol: 'n',  ipa: 'n',   description: 'alveolar nasal — no'},
  {symbol: 'N',  ipa: 'ŋ',   description: 'velar nasal — sing'},
  {symbol: 'J',  ipa: 'ɲ',   description: 'palatal nasal — canyon'},
  {symbol: 'l',  ipa: 'l',   description: 'alveolar lateral — let'},
  {symbol: 'L',  ipa: 'ʎ',   description: 'palatal lateral — million'},
  {symbol: 'r',  ipa: 'r',   description: 'alveolar trill — rolled r'},
  {symbol: 'R',  ipa: 'ʁ',   description: 'uvular fricative — French r'},
  {symbol: 'j',  ipa: 'j',   description: 'palatal approximant — yes'},
  {symbol: 'w',  ipa: 'w',   description: 'labial-velar approximant — wet'},
  {symbol: '?',  ipa: 'ʔ',   description: 'glottal stop'},
];

const REF_COLUMNS = [
  {key: 'symbol',      label: 'eSpeak-ng'},
  {key: 'ipa',         label: 'IPA'},
  {key: 'description', label: 'Description'},
];

const parseImportText = (text) =>
  text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map((line, idx) => {
      const parts = line.split(/\s+/);
      return {id: idx, phrase: parts[0] || '', pronunciation: parts.slice(1).join(' ') || '', status: null};
    })
    .filter(item => item.phrase);

export const PhonemizeCreateModal = ({open, onClose, onSuccess, projectId}) => {
  const [mode, setMode] = useState('create');

  const [phrase, setPhrase] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [saving, setSaving] = useState(false);

  const [importText, setImportText] = useState('');
  const [importItems, setImportItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const [copiedCell, setCopiedCell] = useState(null);

  const handleTogglePopover = (e) => setAnchorEl(prev => prev ? null : e.currentTarget);
  const handleClosePopover = () => setAnchorEl(null);

  const handleCopy = (text, cellKey) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedCell(cellKey);
    setTimeout(() => setCopiedCell(null), 1500);
  };

  const handleImportTextChange = (e) => {
    const text = e.target.value;
    setImportText(text);
    setImportItems(parseImportText(text));
  };

  const handleUpdateItem = (id, field, value) =>
    setImportItems(prev => prev.map(item => item.id === id ? {...item, [field]: value} : item));

  const handleRemoveItem = (id) =>
    setImportItems(prev => prev.filter(item => item.id !== id));

  const handleSave = async () => {
    if (!phrase.trim() || !pronunciation.trim()) {
      toast.error("Both phrase and pronunciation are required");
      return;
    }
    setSaving(true);
    try {
      await VocabularyRepository.create({
        phrase: phrase.trim(),
        pronunciation: pronunciation.trim(),
        type: 'PHONEMIZER',
        flowUid: projectId,
      });
      toast.success("Phonemizer entry created successfully");
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create phonemizer entry");
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    const validItems = importItems.filter(i => i.phrase.trim() && i.pronunciation.trim());
    if (validItems.length === 0) {
      toast.error("No valid items to import");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    let done = 0;

    for (const item of validItems) {
      setImportItems(prev => prev.map(i => i.id === item.id ? {...i, status: 'uploading'} : i));
      try {
        await VocabularyRepository.create({
          phrase: item.phrase.trim(),
          pronunciation: item.pronunciation.trim(),
          type: 'PHONEMIZER',
          flowUid: projectId,
        });
        setImportItems(prev => prev.map(i => i.id === item.id ? {...i, status: 'success'} : i));
      } catch {
        setImportItems(prev => prev.map(i => i.id === item.id ? {...i, status: 'error'} : i));
      }
      done++;
      setUploadProgress(Math.round((done / validItems.length) * 100));
    }

    setUploading(false);
    onSuccess?.();
  };

  const handleClose = () => {
    setPhrase('');
    setPronunciation('');
    setImportText('');
    setImportItems([]);
    setUploadProgress(0);
    setMode('create');
    onClose();
  };

  const validImportCount = importItems.filter(i => i.phrase.trim() && i.pronunciation.trim()).length;
  const isImportDone = importItems.length > 0 && uploading === false && uploadProgress === 100;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{pb: 1}}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              {mode === 'create' ? 'Create Phonemizer Entry' : 'Import Phonemizer Entries'}
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, val) => val && !uploading && setMode(val)}
              size="small"
            >
              <ToggleButton value="create" sx={{px: 2, fontSize: '0.75rem', textTransform: 'none'}}>Create</ToggleButton>
              <ToggleButton value="import" sx={{px: 2, fontSize: '0.75rem', textTransform: 'none'}}>Import</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {mode === 'create' ? (
            <Stack gap={2.5} sx={{pt: 1}}>
              <Alert severity="info" icon={false} sx={{borderRadius: 2}}>
                <Typography variant="body2">
                  The <strong>Phonemizer</strong> maps specific words or phrases to their exact phonetic
                  pronunciation, ensuring the TTS engine speaks them correctly. Use it when a word is
                  consistently mispronounced — for example, mapping a brand name, abbreviation, or
                  foreign word to its intended spoken form.
                </Typography>
              </Alert>

              <Box>
                <Typography variant="caption" color={grey[600]} sx={{mb: 1, display: 'block'}}>
                  <strong>Phrase</strong> — the exact word or text the TTS will encounter in the input
                  (e.g. "VIP", "WiFi", "Đorđe").
                </Typography>
                <TextField
                  placeholder="e.g. VIP"
                  value={phrase}
                  onChange={e => setPhrase(e.target.value)}
                  fullWidth
                  InputLabelProps={{shrink: true}}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                />
              </Box>

              <Box>
                <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{mb: 1}}>
                  <Typography variant="caption" color={grey[600]} sx={{flex: 1}}>
                    <strong>Pronunciation</strong> — the eSpeak-ng phoneme string the TTS will use to
                    produce audio (e.g. "v I p" for VIP). Use X-SAMPA-based eSpeak-ng symbols; click{' '}
                    <HugeiconsIcon icon={KeyboardIcon} size={11} style={{verticalAlign: 'middle'}}/>{' '}
                    to see the full phoneme reference table.
                  </Typography>
                  <Tooltip title="Phoneme character reference" arrow>
                    <IconButton
                      size="small"
                      onClick={handleTogglePopover}
                      sx={{flexShrink: 0, mt: -0.25, color: anchorEl ? 'primary.main' : 'inherit'}}
                    >
                      <HugeiconsIcon icon={KeyboardIcon} size={18}/>
                    </IconButton>
                  </Tooltip>
                </Stack>
                <TextField
                  placeholder="e.g. v I p"
                  value={pronunciation}
                  onChange={e => setPronunciation(e.target.value)}
                  fullWidth
                  InputLabelProps={{shrink: true}}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                />
              </Box>
            </Stack>
          ) : (
            <Stack gap={2} sx={{pt: 1}}>
              <Typography variant="caption" color={grey[600]}>
                Paste entries below — one per line, phrase followed by eSpeak-ng phoneme symbols separated by spaces.{' '}
                Example: <code>VIP v I p</code>
              </Typography>
              <TextField
                multiline
                rows={4}
                placeholder={"VIP v I p\nWiFi w aI f aI\nexample I g z A: m p @l"}
                value={importText}
                onChange={handleImportTextChange}
                fullWidth
                disabled={uploading}
                sx={{'& .MuiInputBase-input': {fontFamily: 'monospace', fontSize: '0.85rem'}}}
              />

              {importItems.length > 0 && (
                <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden'}}>
                  <Box sx={{maxHeight: 280, overflow: 'auto'}}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{fontWeight: 600, width: 32, bgcolor: 'background.paper'}}>#</TableCell>
                          <TableCell sx={{fontWeight: 600, bgcolor: 'background.paper'}}>Phrase</TableCell>
                          <TableCell sx={{fontWeight: 600, bgcolor: 'background.paper'}}>Pronunciation</TableCell>
                          <TableCell sx={{width: 40, bgcolor: 'background.paper'}}/>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importItems.map((item, idx) => {
                          const isSuccess = item.status === 'success';
                          const isError = item.status === 'error';
                          const isUploading = item.status === 'uploading';
                          const inputStyle = isSuccess ? {textDecoration: 'line-through', color: grey[400]} : {};
                          return (
                            <TableRow
                              key={item.id}
                              sx={{
                                opacity: isUploading ? 0.5 : 1,
                                bgcolor: isError ? 'rgba(244,67,54,0.05)' : isSuccess ? 'rgba(102,218,38,0.04)' : 'inherit',
                                transition: 'background-color 0.2s',
                              }}
                            >
                              <TableCell sx={{color: grey[400], fontSize: '0.75rem', py: 0.5}}>{idx + 1}</TableCell>
                              <TableCell sx={{p: 0.5}}>
                                <Input
                                  value={item.phrase}
                                  onChange={e => handleUpdateItem(item.id, 'phrase', e.target.value)}
                                  disabled={uploading}
                                  disableUnderline
                                  size="small"
                                  inputProps={{style: {fontSize: '0.85rem', ...inputStyle}}}
                                  sx={{
                                    px: 1, py: 0.25, borderRadius: 1, width: '100%',
                                    bgcolor: uploading ? 'transparent' : grey[50],
                                    '&:hover': {bgcolor: uploading ? 'transparent' : grey[100]},
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{p: 0.5}}>
                                <Input
                                  value={item.pronunciation}
                                  onChange={e => handleUpdateItem(item.id, 'pronunciation', e.target.value)}
                                  disabled={uploading}
                                  disableUnderline
                                  size="small"
                                  inputProps={{style: {fontSize: '0.85rem', ...inputStyle}}}
                                  sx={{
                                    px: 1, py: 0.25, borderRadius: 1, width: '100%',
                                    bgcolor: uploading ? 'transparent' : grey[50],
                                    '&:hover': {bgcolor: uploading ? 'transparent' : grey[100]},
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{p: 0.5, width: 40}}>
                                {isSuccess ? (
                                  <HugeiconsIcon icon={CheckIcon} size={16} color="#66DA26"/>
                                ) : isError ? (
                                  <Tooltip title="Failed to save">
                                    <Box component="span" sx={{color: 'error.main', display: 'flex'}}>
                                      <HugeiconsIcon icon={Cancel01Icon} size={16}/>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <IconButton size="small" onClick={() => handleRemoveItem(item.id)} disabled={uploading}>
                                    <HugeiconsIcon icon={Cancel01Icon} size={14}/>
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 2}}>
          <Button onClick={handleClose} variant="ghost">
            {isImportDone ? 'Close' : 'Cancel'}
          </Button>
          {mode === 'create' ? (
            <Button
              onClick={handleSave}
              variant="modal"
              disabled={saving || !phrase.trim() || !pronunciation.trim()}
            >
              {saving ? 'Saving…' : 'Create'}
            </Button>
          ) : (
            <Button
              onClick={handleImport}
              variant="modal"
              disabled={uploading || validImportCount === 0 || isImportDone}
            >
              {uploading
                ? `Uploading… ${uploadProgress}%`
                : `Import${validImportCount > 0 ? ` (${validImportCount})` : ''}`
              }
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{sx: {borderRadius: 2, minWidth: 500}}}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between"
               sx={{px: 2, pt: 1.5, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider'}}>
          <Typography variant="subtitle2" fontWeight={600}>Phoneme character reference</Typography>
          <IconButton size="small" onClick={handleClosePopover}>
            <HugeiconsIcon icon={Cancel01Icon} size={16}/>
          </IconButton>
        </Stack>
        <Box sx={{maxHeight: 300, overflow: 'auto'}}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {REF_COLUMNS.map(col => (
                  <TableCell key={col.key} sx={{fontWeight: 700, fontSize: '0.75rem', bgcolor: 'background.paper'}}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PHONEMIZER_CHARS.map((row, idx) => (
                <TableRow key={row.letter} hover>
                  {REF_COLUMNS.map(col => {
                    const cellKey = `${idx}-${col.key}`;
                    const isCopied = copiedCell === cellKey;
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          fontSize: '0.8rem', position: 'relative', pr: 1,
                          '&:hover .copy-btn': {opacity: 1},
                        }}
                      >
                        {row[col.key]}
                        <IconButton
                          size="small"
                          className="copy-btn"
                          onClick={() => handleCopy(row[col.key], cellKey)}
                          sx={{
                            opacity: 0,
                            position: 'absolute', right: 2, top: '50%',
                            transform: 'translateY(-50%)', p: 0.25,
                            transition: 'opacity 0.15s',
                          }}
                        >
                          {isCopied ? <HugeiconsIcon icon={CheckIcon} size={13} color="green"/> : <HugeiconsIcon icon={Copy01Icon} size={13}/>} 
                        </IconButton>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Popover>
    </>
  );
};
